import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, XCircle, Clock, AlertTriangle, Send, FileText, Car, Building2, Calendar, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  borrador: { label: "Borrador", icon: FileText, bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", dot: "bg-slate-400", description: "Este bono aun no ha sido emitido." },
  emitido: { label: "Vigente", icon: CheckCircle, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500", description: "Este bono esta vigente y puede ser canjeado." },
  enviado: { label: "Vigente", icon: Send, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500", description: "Este bono esta vigente y puede ser canjeado." },
  entregado: { label: "Vigente", icon: CheckCircle, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500", description: "Este bono esta vigente y puede ser canjeado." },
  canjeado: { label: "Canjeado", icon: ShieldCheck, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500", description: "Este bono ya fue canjeado correctamente." },
  caducado: { label: "Caducado", icon: Clock, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500", description: "Este bono ha superado su fecha de validez." },
  anulado: { label: "Anulado", icon: XCircle, bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500", description: "Este bono ha sido anulado y no es valido." },
};

const TYPE_LABELS = { lavado: "Bono de Lavado", cortesia: "Bono de Cortesia", atencion_comercial: "Atencion Comercial", cheque_regalo: "Cheque Regalo", personalizado: "Bono Personalizado" };

export default function VoucherValidation() {
  const { code } = useParams();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const r = await fetch(`${API}/api/vouchers/validate/${code}`);
        if (!r.ok) {
          if (r.status === 404) setError("not_found");
          else setError("server");
          return;
        }
        setVoucher(await r.json());
      } catch { setError("network"); }
      finally { setLoading(false); }
    };
    validate();
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">
            {error === "not_found" ? "Bono no encontrado" : "Error de validacion"}
          </h1>
          <p className="text-sm text-slate-500">
            {error === "not_found" ? `No se ha encontrado ningun bono con el codigo "${code}".` : "No se ha podido verificar el bono. Intentelo mas tarde."}
          </p>
          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">ASCAUTO — Servicio Oficial</p>
          </div>
        </div>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[voucher.status] || STATUS_CONFIG.borrador;
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="voucher-validation-page">
      <div className="bg-white rounded-lg shadow-lg max-w-sm w-full overflow-hidden">
        <div className="bg-slate-900 p-5 text-center">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">ASCAUTO</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider mt-0.5">Verificacion de bono</p>
        </div>

        <div className={`mx-4 -mt-4 rounded-lg ${cfg.bg} border ${cfg.border} p-5 text-center relative z-10`} data-testid="validation-status-card">
          <div className={`w-14 h-14 rounded-full ${cfg.bg} border-2 ${cfg.border} flex items-center justify-center mx-auto mb-3`}>
            <Icon className={`w-7 h-7 ${cfg.text}`} />
          </div>
          <p className={`text-lg font-bold ${cfg.text} uppercase tracking-wide`} data-testid="validation-status">{cfg.label}</p>
          <p className="text-xs text-slate-500 mt-1">{cfg.description}</p>
        </div>

        <div className="p-5 space-y-3">
          <div className="bg-slate-50 rounded-md p-3">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Codigo</p>
            <p className="text-base font-mono font-bold text-slate-900 mt-0.5" data-testid="validation-code">{voucher.code}</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {voucher.type && (
              <div className="bg-slate-50 rounded-md p-2.5">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Tipo</p>
                <p className="text-xs text-slate-700 font-semibold mt-0.5">{TYPE_LABELS[voucher.type] || voucher.type}</p>
              </div>
            )}
            {voucher.center && (
              <div className="bg-slate-50 rounded-md p-2.5">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1"><Building2 className="w-2.5 h-2.5" /> Centro</p>
                <p className="text-xs text-slate-700 font-semibold mt-0.5">{voucher.center}</p>
              </div>
            )}
          </div>

          {voucher.client_name && (
            <div className="bg-slate-50 rounded-md p-2.5">
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Titular</p>
              <p className="text-xs text-slate-700 font-semibold mt-0.5">{voucher.client_name}</p>
            </div>
          )}

          {voucher.plate && (
            <div className="bg-slate-50 rounded-md p-2.5">
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1"><Car className="w-2.5 h-2.5" /> Matricula</p>
              <p className="text-xs text-slate-700 font-mono font-bold mt-0.5">{voucher.plate}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {voucher.issue_date && (
              <div className="bg-slate-50 rounded-md p-2.5">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> Emitido</p>
                <p className="text-xs text-slate-700 font-semibold mt-0.5">{voucher.issue_date}</p>
              </div>
            )}
            {voucher.expiry_date && (
              <div className="bg-slate-50 rounded-md p-2.5">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> Caduca</p>
                <p className={`text-xs font-bold mt-0.5 ${voucher.status === "caducado" ? "text-amber-700" : "text-slate-700"}`}>{voucher.expiry_date}</p>
              </div>
            )}
          </div>

          {voucher.redeemed_at && (
            <div className="bg-emerald-50 rounded-md p-2.5 border border-emerald-100">
              <p className="text-[9px] text-emerald-600 font-bold uppercase tracking-wider flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> Canjeado</p>
              <p className="text-xs text-emerald-700 font-semibold mt-0.5">{voucher.redeemed_at.split("T")[0]}</p>
            </div>
          )}
        </div>

        <div className="bg-slate-50 border-t border-slate-100 px-5 py-4 text-center">
          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.15em]">ASCAUTO — Servicio Oficial Stellantis</p>
          <p className="text-[8px] text-slate-300 mt-1">Verificacion automatica</p>
        </div>
      </div>
    </div>
  );
}
